import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { TICKETS } from "../lib/content";
import { toast } from "sonner";
import { Lock } from "lucide-react";
import { useT } from "../lib/i18n";

function formatPhone(raw) {
  let v = raw.replace(/[^\d+]/g, "");
  if (v && !v.startsWith("+")) v = "+" + v;
  const m = v.match(/^\+(\d{1,3})(\d{0,3})(\d{0,3})(\d{0,4})$/);
  if (!m) return v.slice(0, 16);
  return ["+" + m[1], m[2], m[3], m[4]].filter(Boolean).join(" ");
}

export default function RegistrationModal({ open, onOpenChange, defaultTicket }) {
  const { t } = useT();
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("+48 ");
  const [telegram, setTelegram] = useState("");
  const [ticket, setTicket] = useState(defaultTicket || "standard");
  const [agree, setAgree] = useState(true);
  const [loading] = useState(false);

  useEffect(() => {
    if (defaultTicket) setTicket(defaultTicket);
  }, [defaultTicket, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedTicket?.paymentUrl) {
      window.location.href = selectedTicket.paymentUrl;
      return;
    }
    if (selectedTicket?.soldOut) {
      toast.error(t("toasts.closed") || "Sprzedaż biletów na to wydarzenie została zakończona.");
      return;
    }
    toast.error(t("toasts.closed") || "Sprzedaż biletów na to wydarzenie została zakończona.");
  };

  const selectedTicket = TICKETS.find((tk) => tk.key === ticket);
  const ticketTrans = t(`pricing.tickets.${ticket}`) || {};

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="registration-modal" className="max-w-xl bg-[#F8F5F2] border border-[#E2D8CF] rounded-none p-0 overflow-hidden">
        <div className="bg-[#6E1A24] text-[#F8F5F2] px-6 py-5">
          <DialogHeader className="space-y-1">
            <DialogTitle className="font-display uppercase tracking-wide text-2xl">{t("modal.title")}</DialogTitle>
            <DialogDescription className="text-[#F8F5F2]/80 text-sm">{t("modal.desc")}</DialogDescription>
          </DialogHeader>
        </div>

        <form onSubmit={handleSubmit} noValidate className="px-6 py-6 space-y-6" data-testid="registration-form">
          <div>
            <label className="eyebrow text-ink2 block mb-1">{t("modal.l_name")}</label>
            <input data-testid="input-fullname" className="input-underline" placeholder={t("modal.p_name")} value={fullName} onChange={(e) => setFullName(e.target.value)} />
          </div>

          <div>
            <label className="eyebrow text-ink2 block mb-1">{t("modal.l_phone")}</label>
            <input data-testid="input-phone" className="input-underline" placeholder={t("modal.p_phone")} value={phone} onChange={(e) => setPhone(formatPhone(e.target.value))} inputMode="tel" />
          </div>

          <div>
            <label className="eyebrow text-ink2 block mb-1">{t("modal.l_tg")}</label>
            <input data-testid="input-telegram" className="input-underline" placeholder={t("modal.p_tg")} value={telegram} onChange={(e) => setTelegram(e.target.value)} />
            <p className="text-xs text-ink2 mt-2">{t("modal.help_tg")}</p>
          </div>

          <div>
            <label className="eyebrow text-ink2 block mb-2">{t("modal.l_ticket")}</label>
            <Select value={ticket} onValueChange={setTicket}>
              <SelectTrigger data-testid="select-ticket" className="rounded-none bg-transparent border-0 border-b border-[#1A1A1A] focus:border-[#6E1A24] focus:ring-0 px-0 h-12 text-base">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none bg-[#F8F5F2] border-[#E2D8CF]">
                {TICKETS.filter((tk) => !tk.hidden).map((tk) => {
                  const name = t(`pricing.tickets.${tk.key}.name`) || tk.key;
                  return (
                    <SelectItem key={tk.key} value={tk.key} data-testid={`select-ticket-option-${tk.key}`} className="rounded-none">
                      {name} — {tk.price} PLN
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            {ticketTrans.name && (
              <p className="text-xs text-ink2 mt-2">{ticketTrans.name}{ticketTrans.sub ? ` · ${ticketTrans.sub}` : ""}</p>
            )}
          </div>

          <div className="bg-[#F2EBE5] border border-[#E2D8CF] p-4 flex items-center justify-between">
            <div>
              <div className="eyebrow text-ink2">{t("modal.total")}</div>
              <div className="font-display text-3xl">{selectedTicket?.price} PLN</div>
            </div>
            <div className="text-right text-xs text-ink2 max-w-[60%]">{t("modal.pay_note")}</div>
          </div>

          <label className="flex items-start gap-3 text-xs text-ink2 cursor-pointer">
            <input data-testid="checkbox-agree" type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-0.5 accent-[#6E1A24]" />
            <span>{t("modal.agree")}</span>
          </label>

          <button type="submit" disabled={loading} data-testid="submit-payment-btn" className="btn-primary w-full px-8 py-4 inline-flex items-center justify-center gap-2 disabled:opacity-70">
            {loading ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> {t("modal.pay_loading")}</>
            ) : (
              <><Lock className="w-4 h-4" /> {t("modal.pay_btn")}</>
            )}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
